import React, { useCallback, useState, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { toJS } from "mobx";

import { CurrentJobStorageModel } from "../../../services/job/CurrentJobStorage";
import { translationService } from "../../../services/translation/TranslationService";
import DialogGrid from "../../../components/dialog-grid/DialogGrid";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { ProjectsSummaryStorageModel } from "../../../services/project/ProjectsSummaryStorage";
import { SewerObjectsSummaryStorageModel } from "../../../services/sewer-object/SewerObjectsSummaryStorage";
import { api } from "../../../services/api/ApiService";
import { JobSewerObjectsStorage } from "../../../services/sewer-object/JobSewerObjectsStorage";
import { JobsStorage } from "../../../services/job/JobsStorage";
import { UsersSummaryStorage } from "../../../services/user/UsersSummaryStorage";
import useUrlParams from "../../../services/url/UrlParamsService";
import { JobResponse } from "../../../api-models/response/job/JobResponse";
import { snackbarNotificationsService } from "../../../services/notifications/SnackbarNotificationsService";
import { Dialog } from "../../../components/dialog/Dialog";
import { UserRolesStorageModel } from "../../../services/user/UserRolesStorage";
import ListOfChoices from "../../../components/list-of-choices/ListOfChoices";
import { JobStatus, JobPriority } from "../../../generated/Jobs";
import { ProjectStatus } from "../../../generated/Projects";

interface ChangeJobStatusButtonModalProps {
  isVisible: boolean;
  toggleModal: () => void;
  jobsStorage: JobsStorage;
}

export function ChangeJobStatusButtonModal(props: ChangeJobStatusButtonModalProps) {
  const { handleErrorInSnackbar } = useErrorHandler();

  const { isVisible, toggleModal, jobsStorage } = { ...props };

  const [status, setStatus] = useState("");

  const getJobStatuses = useCallback(() => {
    const statuses = Object.keys(JobStatus).filter((status: any) => isNaN(status));
    return statuses.map((status) => {
      return {
        nameKey: `general.jobs.status.${status}`,
        value: JobStatus[status as any],
      };
    });
  }, []);

  const handleSubmit = useCallback(async () => {
      const jobsCount = jobsStorage.selectedItemsCount;
      try {
        await jobsStorage.changeJobsStatus(jobsStorage.selectedItems, status);
        snackbarNotificationsService.showSuccess("jobs.ChangeJobStatusButtonModal.success", {
          jobsCount: jobsCount,
          jobStatus: translationService.translate(
            `general.jobs.status.${JobStatus[status]}`
          ),
        });
      } catch (error) {
        const errorContext = { jobsCount: jobsCount };
        const applicationError = error as ApplicationError;
        applicationError.withContext(errorContext);
        applicationError.withErrorMessage("default-error.jobs.change-status");
        handleErrorInSnackbar(applicationError);
      }
      toggleModal();
      setStatus("");
    },
    [jobsStorage, toggleModal, status, handleErrorInSnackbar]
  );

  const handleClose = useCallback(() => {
    toggleModal();
    setStatus("");
  }, [toggleModal]);

  return (
    <Dialog
      isVisible={isVisible}
      sidePosition
      onHide={handleClose}
      titleKey="jobs.ChangeJobStatusButtonModal.title"
      buttonOk={{
        textKey: "jobs.ChangeJobStatusButtonModal.button.ok",
        action: handleSubmit,
        disabled: !status,
      }}
      buttonCancel={{
        textKey: "general.cancel",
        action: handleClose,
      }}
    >
      <ListOfChoices
          id="choose-job-status-option-field"
          options={getJobStatuses()}
          instructionKey={"jobs.ChangeJobStatusButtonModal.instruction"}
          select={(val) => setStatus(val)}
      />
    </Dialog>
  );
}

interface ChangeJobPriorityButtonModalProps {
  isVisible: boolean;
  toggleModal: () => void;
  jobsStorage: JobsStorage;
}

export const ChangeJobPriorityButtonModal = function (props: ChangeJobPriorityButtonModalProps) {
  const { handleErrorInSnackbar } = useErrorHandler();

  const { isVisible, toggleModal, jobsStorage } = { ...props };

  const [priority, setPriority] = useState("");

  const getJobPriorities = useCallback(() => {
    const priorities = Object.keys(JobPriority).filter((priority: any) => isNaN(priority));
    return priorities.map((priority) => {
      return {
        nameKey: `general.jobs.priority.${priority}`,
        value: JobPriority[priority as any],
      };
    });
  }, []);

  const handleSubmit = useCallback(async () => {
      const jobsCount = jobsStorage.selectedItemsCount;
      try {
        await jobsStorage.changeJobsPriority(jobsStorage.selectedItems, priority);
        snackbarNotificationsService.showSuccess("jobs.ChangeJobPriorityButtonModal.success", {
          jobsCount: jobsCount,
          jobPriority: translationService.translate(
            `general.jobs.priority.${JobPriority[priority]}`
          ),
        });
      } catch (error) {
        const errorContext = { jobsCount: jobsCount };
        const applicationError = error as ApplicationError;
        applicationError.withContext(errorContext);
        applicationError.withErrorMessage("default-error.jobs.change-priority");
        handleErrorInSnackbar(applicationError);
      }
      toggleModal();
      setPriority("");
    },
    [jobsStorage, toggleModal, priority, handleErrorInSnackbar]
  );

  const handleClose = useCallback(() => {
    toggleModal();
    setPriority("");
  }, [toggleModal]);

  return (
    <Dialog
      isVisible={isVisible}
      sidePosition
      onHide={handleClose}
      titleKey="jobs.ChangeJobPriorityButtonModal.title"
      buttonOk={{
        textKey: "jobs.ChangeJobPriorityButtonModal.button.ok",
        action: handleSubmit,
        disabled: !priority,
      }}
      buttonCancel={{
        textKey: "general.cancel",
        action: handleClose,
      }}
    >
      <ListOfChoices
          id="choose-job-priority-option-field"
          options={getJobPriorities()}
          instructionKey={"jobs.ChangeJobPriorityButtonModal.instruction"}
          select={(val) => setPriority(val)}
      />
    </Dialog>
  );
};

interface AssignJobToUserButtonModalProps {
  isVisible: boolean;
  userFromProjectId?: number;
  toggleModal: () => void;
  jobsStorage: JobsStorage;
}

export const AssignJobToUserButtonModal = observer(function (
  props: AssignJobToUserButtonModalProps
) {
  const { handleErrorInSnackbar } = useErrorHandler();

  const { isVisible, userFromProjectId: projectId, toggleModal, jobsStorage } = { ...props };

  const [userRolesStorage] = useState(() => UserRolesStorageModel.create({}, { api }));

  const [usersSummaryStorage] = useState(() =>
    UsersSummaryStorage.create({ useGridFiltering: true, autoLoad: false }, { api })
  );

  useEffect(() => {
    if (userRolesStorage.isLoading) {
      return;
    }

    usersSummaryStorage.setUserRoleOptions(toJS(userRolesStorage.userRoles));

    async function load() {
      await usersSummaryStorage.load();
    }
    load();
  }, [userRolesStorage.isLoading, userRolesStorage.userRoles, usersSummaryStorage]);

  useEffect(() => {
    async function setStorageFilter() {
      if (isVisible)
        await usersSummaryStorage.setContextFilter({
          excludeGuest: true,
          projectId: projectId || undefined,
          inProject: !!projectId || undefined,
          includeInvited: false,
          includeDeleted: false,
        });
    }

    setStorageFilter();
  }, [isVisible, usersSummaryStorage, projectId]);

  const onCloseModal = useCallback(() => {
    usersSummaryStorage.resetDataQuery();
    usersSummaryStorage.resetGridFilter();
    toggleModal();
  }, [toggleModal, usersSummaryStorage]);

  const handleSubmit = useCallback(async () => {
    const selectedUser = usersSummaryStorage.selectedItems[0];
    if (!selectedUser) return;

    const jobsCount = jobsStorage.selectedItems.length;

    try {
      await jobsStorage.assignUserToJobs(jobsStorage.selectedItems, selectedUser);
      snackbarNotificationsService.showSuccess("jobs.AssignJobToUserButtonModal.success", {
        jobsCount,
      });
      usersSummaryStorage.resetGridFilter();
    } catch (error) {
      const errorContext = { jobsCount };
      const applicationError = error as ApplicationError;
      applicationError.withContext(errorContext);
      applicationError.withErrorMessage("default-error.jobs.assign-user");
      handleErrorInSnackbar(applicationError);
    }
    toggleModal();
  }, [usersSummaryStorage, toggleModal, jobsStorage, handleErrorInSnackbar]);

  return (
    <Dialog
      isVisible={isVisible}
      sidePosition
      onHide={onCloseModal}
      titleKey="jobs.AssignJobToUserButtonModal.title"
      buttonOk={{
        textKey: "jobs.AssignJobToUserButtonModal.button.ok",
        action: handleSubmit,
        disabled: !usersSummaryStorage.anyItemsSelected,
      }}
      buttonCancel={{
        textKey: "general.cancel",
        action: onCloseModal,
      }}
    >
      <DialogGrid
        itemTypeKey="general.users"
        dataGridStorage={usersSummaryStorage}
        singleSelection
      />
    </Dialog>
  );
});

interface AddJobToProjectButtonModalProps {
  isVisible: boolean;
  toggleModal: () => void;
  jobsStorage: JobsStorage;
}

export const AddJobToProjectButtonModal = observer(function (
  props: AddJobToProjectButtonModalProps
) {
  const { isVisible, toggleModal, jobsStorage } = { ...props };
  const { handleErrorInSnackbar } = useErrorHandler();

  const [projectsSummaryStorage] = useState(() =>
    ProjectsSummaryStorageModel.create({ autoLoad: false, useGridFiltering: true }, { api })
  );

  const handleClose = useCallback(() => {
    toggleModal();
    projectsSummaryStorage.resetGridFilter();
  }, [projectsSummaryStorage, toggleModal]);

  useEffect(() => {
    async function setProjectsSummaryStorageFilter() {
      if (isVisible) await projectsSummaryStorage.setContextFilter({ status: ProjectStatus.Open });
    }

    setProjectsSummaryStorageFilter();
  }, [isVisible, projectsSummaryStorage]);

  const handleSubmit = useCallback(async () => {
    const selectedProject = projectsSummaryStorage.selectedItems[0];
    if (!selectedProject) return;

    const jobsCount = jobsStorage.selectedItemsCount;

    try {
      await jobsStorage.addToProject(jobsStorage.selectedItems, selectedProject);
      snackbarNotificationsService.showSuccess("jobs.AddJobToProjectButtonModal.success", {
        jobsCount,
      });
      projectsSummaryStorage.resetGridFilter();
    } catch (error) {
      const errorContext = { jobsCount };
      const applicationError = error as ApplicationError;
      applicationError.withContext(errorContext);
      applicationError.withErrorMessage("default-error.jobs.add-to-project");
      handleErrorInSnackbar(applicationError);
    }
    toggleModal();
  }, [projectsSummaryStorage, jobsStorage, toggleModal, handleErrorInSnackbar]);

  return (
    <Dialog
      isVisible={isVisible}
      sidePosition
      onHide={handleClose}
      titleKey="jobs.AddJobToProjectButtonModal.title"
      buttonOk={{
        id: "add-job-to-project-button-ok",
        textKey: "jobs.AddJobToProjectButtonModal.button.ok",
        action: handleSubmit,
        disabled: !projectsSummaryStorage.anyItemsSelected,
      }}
      buttonCancel={{
        textKey: "general.cancel",
        action: handleClose,
      }}
    >
      <DialogGrid
        itemTypeKey="general.projects"
        dataGridStorage={projectsSummaryStorage}
        singleSelection
      />
    </Dialog>
  );
});

interface AddExistingSewerObjectsToJobModalProps {
  isVisible: boolean;
  toggleModal: () => void;
  reloadSewerObjectsMap(): Promise<void>;
  jobSewerObjectsStorage: JobSewerObjectsStorage;
}

export const AddExistingSewerObjectsToJobModal = observer(function ({
  isVisible,
  toggleModal,
  reloadSewerObjectsMap,
  jobSewerObjectsStorage,
}: AddExistingSewerObjectsToJobModalProps) {
  const { handleErrorInSnackbar } = useErrorHandler();
  const urlParamsService = useUrlParams();

  const [currentJobStorage] = useState(() =>
    CurrentJobStorageModel.create({ currentJobId: urlParamsService.getJobId()! }, { api })
  );

  const currentJob: JobResponse = useMemo(() => {
    return currentJobStorage.currentJob;
  }, [currentJobStorage.currentJob]);

  const [sewerObjectsSummaryStorage] = useState(() => {
    return SewerObjectsSummaryStorageModel.create(
      { autoLoad: false, useGridFiltering: true },
      { api }
    );
  });

  useEffect(() => {
    async function setSewerObjectsSummaryStorageFilter() {
      if (currentJob.id > 0 && currentJob.project && currentJob.project.id && isVisible) {
        await sewerObjectsSummaryStorage.setContextFilter({
          ProjectId: currentJob.project.id,
          InProject: true,
          JobId: currentJob.id,
          InJob: false,
        });
      }
    }

    if (isVisible) {
      sewerObjectsSummaryStorage.deselectAll();
    }

    setSewerObjectsSummaryStorageFilter();
  }, [currentJob, isVisible, sewerObjectsSummaryStorage]);

  const addSewerObjectsToJob = useCallback(async () => {
    try {
      await jobSewerObjectsStorage.assignSewerObjectsToJob(
        currentJob.id,
        sewerObjectsSummaryStorage.selectedItems
      );
      snackbarNotificationsService.showSuccess(
        "sewer-objects.AddExistingSewerObjectsToJobModal.success",
        {
          sewerObjectsCount: sewerObjectsSummaryStorage.selectedItemsCount,
        }
      );
      await reloadSewerObjectsMap();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error.sewer-objects.add-to-job");
      handleErrorInSnackbar(applicationError);
    }
    toggleModal();
  }, [
    toggleModal,
    jobSewerObjectsStorage,
    currentJob.id,
    sewerObjectsSummaryStorage.selectedItems,
    sewerObjectsSummaryStorage.selectedItemsCount,
    reloadSewerObjectsMap,
    handleErrorInSnackbar,
  ]);

  return (
    <Dialog
      isVisible={isVisible}
      sidePosition
      onHide={toggleModal}
      titleKey="sewer-objects.AddExistingSewerObjectsToJobModal.title"
      buttonOk={{
        id: "add-sewer-objects-to-job-ok-button",
        textKey: "sewer-objects.AddExistingSewerObjectsButton.ok",
        action: addSewerObjectsToJob,
        disabled: !sewerObjectsSummaryStorage.anyItemsSelected,
      }}
      buttonCancel={{
        textKey: "general.cancel",
        action: toggleModal,
      }}
    >
      <DialogGrid
        itemTypeKey="general.sewer-objects"
        dataGridStorage={sewerObjectsSummaryStorage}
      />
    </Dialog>
  );
});
